// Mirror of foundation/users/src/policies/LevelPolicy.js.
//
// This is a lightweight client-side copy of the level constants so
// dashboard components can render the ladder + colours without a
// round-trip. The server is the source of truth for thresholds /
// weights / eligibility (read those via identity.getLevelConfig);
// this file only holds the name/order/visual treatment.

import { rungLabelFor, rungName, levelNumberFromName, RUNG_NAMES, PAID_UNLOCK_LEVEL } from '@moksy/common/src/progression/rungs.js';

// ── What a person READS ─────────────────────────────────────────────
// The twenty names below are stored identifiers (user.level = 'Guru'),
// so they stay. Every surface renders the RUNG for a level instead —
// six words over the same twenty numbers, from packages/common. See
// docs/brand/MOKSY_LEXICON.md §C4.
export { rungLabelFor, rungName, levelNumberFromName, RUNG_NAMES, PAID_UNLOCK_LEVEL };

/** Accreditation ids L0–L4 → the rung a reviewer reads. */
export const ACC_RUNG = Object.freeze({
  L0: 'Candidate', L1: 'Contributor', L2: 'Reviewer', L3: 'Senior', L4: 'Principal'
});
export const accRungLabel = (accLevel) => ACC_RUNG[String(accLevel || '').toUpperCase()] || String(accLevel || '');

/** Anything a record might hold — a number, "L9", a stored name — as "Senior · Level 9". */
export const displayLevel = (levelOrName, opts) => rungLabelFor(levelOrName, opts);

export const RUNG_COLOR = Object.freeze({
  Candidate:   'bg-gray-100 text-gray-700 border-gray-200',
  Contributor: 'bg-slate-100 text-slate-700 border-slate-200',
  Reviewer:    'bg-blue-50 text-blue-700 border-blue-200',
  Senior:      'bg-indigo-50 text-indigo-700 border-indigo-200',
  Principal:   'bg-amber-100 text-amber-800 border-amber-300',
  Fellow:      'bg-orange-100 text-orange-900 border-orange-300'
});
export const colorForRung = (levelOrName) =>
  RUNG_COLOR[rungName(levelNumberFromName(levelOrName))] || RUNG_COLOR.Candidate;

export const LEVELS = [
  { name: 'Novice',       score: 5 },
  { name: 'Beginner',     score: 10 },
  { name: 'Intermediate', score: 15 },
  { name: 'Professional', score: 20 },
  { name: 'Expert',       score: 25 },
  { name: 'Genius',       score: 30 },
  { name: 'Master',       score: 35 },
  { name: 'Ninja',        score: 40 },
  { name: 'Champion',     score: 45 },
  { name: 'Legend',       score: 50 },
  { name: 'Guru',         score: 55 },
  { name: 'Sage',         score: 60 },
  { name: 'Maestro',      score: 65 },
  { name: 'Architect',    score: 70 },
  { name: 'Visionary',    score: 75 },
  { name: 'Innovator',    score: 80 },
  { name: 'Mystic',       score: 85 },
  { name: 'Virtuoso',     score: 90 },
  { name: 'Guardian',     score: 95 },
  { name: 'Titan',        score: 100 }
];

export const LEVEL_NAMES = LEVELS.map((l) => l.name);

// Visual treatment per level. Bottom rungs are muted; high rungs get
// progressively warmer / more saturated treatment so a user can
// glance and tell where someone sits.
export const LEVEL_COLOR = {
  Novice:       'bg-gray-100 text-gray-700 border-gray-200',
  Beginner:     'bg-gray-100 text-gray-700 border-gray-200',
  Intermediate: 'bg-slate-100 text-slate-700 border-slate-200',
  Professional: 'bg-blue-50 text-blue-700 border-blue-200',
  Expert:       'bg-indigo-50 text-indigo-700 border-indigo-200',
  Genius:       'bg-indigo-100 text-indigo-800 border-indigo-300',
  Master:       'bg-violet-100 text-violet-800 border-violet-300',
  Ninja:        'bg-purple-100 text-purple-800 border-purple-300',
  Champion:     'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300',
  Legend:       'bg-pink-100 text-pink-800 border-pink-300',
  Guru:         'bg-rose-100 text-rose-800 border-rose-300',
  Sage:         'bg-amber-100 text-amber-800 border-amber-300',
  Maestro:      'bg-amber-200 text-amber-900 border-amber-400',
  Architect:    'bg-orange-200 text-orange-900 border-orange-400',
  Visionary:    'bg-orange-300 text-orange-950 border-orange-500',
  Innovator:    'bg-red-200 text-red-900 border-red-400',
  Mystic:       'bg-emerald-100 text-emerald-800 border-emerald-300',
  Virtuoso:     'bg-emerald-200 text-emerald-900 border-emerald-400',
  Guardian:     'bg-teal-200 text-teal-900 border-teal-400',
  Titan:        'bg-yellow-200 text-yellow-900 border-yellow-500'
};

export const colorForLevel = (name) =>
  LEVEL_COLOR[name] || 'bg-gray-100 text-gray-700 border-gray-200';

export const rungForLevel = (name) => {
  const idx = LEVELS.findIndex((l) => l.name === name);
  return idx < 0 ? null : idx + 1;
};

export const meetsEligibility = (currentLevel, eligibilityLevel) => {
  const a = rungForLevel(currentLevel);
  const b = rungForLevel(eligibilityLevel);
  if (a == null || b == null) return false;
  return a >= b;
};

export default {
  LEVELS,
  ACC_RUNG,
  accRungLabel,
  displayLevel,
  RUNG_COLOR,
  colorForRung,
  LEVEL_NAMES,
  LEVEL_COLOR,
  colorForLevel,
  rungForLevel,
  meetsEligibility
};
